'use client';

import type { ChangeEvent, RefObject } from 'react';
import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Loader } from '@/app/components/Loader';
import { getResizedPictureUrl } from '@/app/utils/entities/media';
import { Zoom } from '~/components/commons/Zoom/Zoom';
import { keyboardKeys } from '~/const/keyCodes';
import {
  checkFileUploadAuthorizedFormat,
  checkFileUploadAuthorizedSize,
  checkImageUploadAuthorizedFormat,
  checkWebpConversion,
  fileSizeMegaBytesDefaultLimit,
} from '~/utils/utils';
import { FileUploaderLabel } from './FileUploaderLabel';

export const FileUploader = ({
  id,
  handleInputFileChange,
  path,
  inputLabel,
  isPicture,
  defaultFile,
  fromQuestion = false,
  fromProfile = false,
  buttonRef,
}: {
  id: number | string;
  handleInputFileChange: (fileToUpload: FormData) => Promise<void>;
  path: string;
  isPicture: boolean;
  inputLabel?: string;
  defaultFile?: string;
  fromQuestion?: boolean;
  fromProfile?: boolean;
  buttonRef?: RefObject<any>;
}) => {
  const t = useTranslations();
  const [fileFormatError, setFileFormatError] = useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<string | ArrayBuffer>(null);
  const [fileName, setFileName] = useState<string>(null);
  const [fileType, setFileType] = useState<string>(null);
  const [formData, setFormData] = useState<FormData>(null);
  const [isLoading, setIsLoading] = useState(false);

  const inputfile = useRef<HTMLInputElement>(null);

  const resetState = async () => {
    // Empty input file and reset State
    if (inputfile.current)
      inputfile.current.value = '';
    setFileToUpload(null);
    setFileName(null);
    setFileType(null);
    setIsLoading(false);
    setFormData(null);
    await handleInputFileChange(null);
  };

  const checkFile = (
    file,
    sizeLimit: number,
    checkFormat: (file) => boolean,
    checkSize?: (file, sizeLimit: number) => boolean,
  ) => {
    const isFormatCorrect = checkFormat(file);

    if (!isFormatCorrect) {
      setFileFormatError(true);
      setFileName(null);
      setFileType(null);
      setFileToUpload(null);
      setIsLoading(false);
      return false;
    }
    if (checkSize && !checkSize(file, sizeLimit)) {
      setFileSizeError(true);
      setFileName(null);
      setFileType(null);
      setFileToUpload(null);
      setIsLoading(false);
      return false;
    }
    return true;
  };

  const displaySelectedPicture = (
    reader: FileReader,
    blob: Blob,
    originalFile,
    sizeLimit: number,
  ) => {
    const isWebpConversion = checkWebpConversion(blob);
    const fileToUse = isWebpConversion ? blob : originalFile;
    const isFileCorrect = checkFile(
      fileToUse,
      sizeLimit,
      checkImageUploadAuthorizedFormat,
      checkFileUploadAuthorizedSize,
    );
    if (isFileCorrect) {
      const formData = new FormData();
      formData.append('file', fileToUse, fileToUse.type.replace('/', '.'));
      if (path)
        formData.append('path', path);
      const fileResult = reader.result;
      setIsLoading(false);
      setFileFormatError(false);
      setFileToUpload(fileResult);
      setFileName(originalFile.name);
      setFileType(originalFile.type);
      setFormData(formData);
    }
  };

  const convertPictureToWebp = (
    image: HTMLImageElement,
    reader: FileReader,
    file,
    sizeLimit: number,
  ) => {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0);
    canvas?.toBlob(
      (blob) => {
        displaySelectedPicture(reader, blob, file, sizeLimit);
      },
      'image/webp',
      0.68,
    );
  };

  const selectPicture = (
    e: ChangeEvent<HTMLInputElement>,
    sizeLimit: number,
  ) => {
    const reader = new FileReader();
    const file = e.target.files[0];
    const isFileCorrect = checkFile(
      file,
      sizeLimit,
      checkImageUploadAuthorizedFormat,
    );
    if (isFileCorrect) {
      reader.readAsDataURL(file);
      reader.onloadend = (ev) => {
        const image = new Image();
        image.src = ev.target.result as string;
        image.onload = () => {
          convertPictureToWebp(image, reader, file, sizeLimit);
        };
      };
    }
  };

  const selectDocument = async (
    e: ChangeEvent<HTMLInputElement>,
    sizeLimit: number,
  ) => {
    const reader = new FileReader();
    const formData = new FormData();
    const file = e.target.files[0];
    formData.append('file', file);
    if (path)
      formData.append('path', path);
    const isFileCorrect = checkFile(
      file,
      sizeLimit,
      checkFileUploadAuthorizedFormat,
      checkFileUploadAuthorizedSize,
    );
    if (isFileCorrect) {
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const loadedFile = reader.result;
        setFileFormatError(false);
        setFileToUpload(loadedFile);
        setFileName(file.name);
        setFileType(file.type);
        setIsLoading(false);
        setFormData(formData);
      };
    } else {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);

    if (isPicture) {
      selectPicture(event, fileSizeMegaBytesDefaultLimit);
    } else {
      selectDocument(event, fileSizeMegaBytesDefaultLimit);
    }
  };

  const onInputFileChange = async () => {
    try {
      await handleInputFileChange(formData);
    } catch (error) {
      resetState();
      throw error;
    }
  };

  const idValue = id ? `-${id}` : '';

  const fileUploaderTitle = isPicture
    ? 'PICTURE_UPLOADER.TITLE'
    : 'FILE_UPLOADER.TITLE';
  const fileUploaderAccept = isPicture
    ? 'image/*'
    : 'image/*,.gpx,application/pdf';
  const fileUploaderChooseFile = isPicture
    ? 'PICTURE_UPLOADER.CHOOSE_PICTURE'
    : 'FILE_UPLOADER.CHOOSE_FILE';
  const fileUploaderError = isPicture
    ? 'PICTURE_UPLOADER.ERROR'
    : 'FILE_UPLOADER.ERROR';

  useEffect(() => {
    if (fileSizeError || fileFormatError)
      resetState();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileFormatError, fileSizeError]);

  useEffect(() => {
    if (formData) {
      onInputFileChange();
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  if (isLoading)
    return <Loader size="small" centered />;

  return (
    <div
      data-testid="file-uploader"
      className={classNames('control-upload', {
        'control-upload--error': fileFormatError || fileSizeError,
        'control-upload--large': fromQuestion,
      })}
    >
      <div
        className="control-upload__add"
        title={t(fileUploaderTitle, {
          sizeLimit: fileSizeMegaBytesDefaultLimit,
        })}
      >
        <input
          aria-label={t(fileUploaderTitle)}
          id={`uploader${idValue}`}
          name={`uploader${idValue}`}
          type="file"
          className="inputStyleFile"
          data-cy="fileUpload"
          accept={fileUploaderAccept}
          onChange={handleFileSelect}
          ref={inputfile}
          tabIndex={-1}
        />
        {!fromQuestion && (
          <FileUploaderLabel
            idValue={idValue}
            inputLabel={inputLabel}
            fileUploaderChooseFile={fileUploaderChooseFile}
          />
        )}
        {!fileToUpload && fromQuestion && !defaultFile && (
          <label
            htmlFor={`uploader${idValue}`}
            className="control-upload__action"
          >
            {!fileFormatError && !fileSizeError && (
              <div className="control-upload__circle-icon">
                <VpIcon name={isPicture ? 'image' : 'file'} size={48} />
              </div>
            )}
            {(fileFormatError || fileSizeError) && (
              <div className="control-upload__error">
                <VpIcon name="emotion-sad" size={48} />
                {fileFormatError && <span>{t(fileUploaderError)}</span>}
                {fileSizeError && (
                  <span>
                    {t('FILE_UPLOADER.ERROR_2', {
                      sizeLimit: fileSizeMegaBytesDefaultLimit,
                    })}
                  </span>
                )}
              </div>
            )}
            <VpButton
              ref={buttonRef}
              variant="secondary"
              startSlot={<VpIcon name="add" />}
              onClick={() => inputfile.current.click()}
            >
              {t(fileUploaderChooseFile)}
            </VpButton>
          </label>
        )}
      </div>

      {(fileFormatError || fileSizeError) && !fromQuestion && (
        <div className="control-upload__error">
          <span className="control-upload__error-icon">
            <VpIcon name="warning-error" size={16} />
          </span>
          {' '}
          {fileFormatError && <span>{t(fileUploaderError)}</span>}
          {fileSizeError && (
            <span>
              {t('FILE_UPLOADER.ERROR_2', { sizeLimit: fileSizeMegaBytesDefaultLimit })}
            </span>
          )}
        </div>
      )}

      {
        // DEFAULT PICTURE
        isPicture && defaultFile && !fileToUpload && (
          <div className="control-upload__preview">
            <span style={{ backgroundImage: `url(${defaultFile})` }}>
              <img data-testid="default-image" src={defaultFile} alt="" />
              <Zoom mainSrc={getResizedPictureUrl(defaultFile)} />
            </span>
          </div>
        )
      }

      {
        // DEFAULT FILE
        !isPicture && defaultFile && !fileToUpload && (
          <a
            className="control-upload__file"
            href={defaultFile}
            rel="noopener noreferrer"
            target="_blank"
          >
            <VpIcon name="file" size={16} />
            <span className="control-upload__file__label">
              {t('FILE_UPLOADER.FILE_LABEL')}
            </span>
            <span className="control-upload__file__extension">
              .
              {defaultFile.split('.').pop()}
            </span>
          </a>
        )
      }

      {
        // PICTURE PREVIEW
        !fromProfile
        && fileToUpload
        && (fileType === 'image/png'
          || fileType === 'image/gif'
          || fileType === 'image/webp'
          || fileType === 'image/jpg'
          || fileType === 'image/jpeg') && (
          <div className="control-upload__preview">
            <span style={{ backgroundImage: `url(${fileToUpload})` }}>
              <img data-testid="file-to-upload" src={fileToUpload as string} alt="" />
              {defaultFile && (
                <Zoom mainSrc={getResizedPictureUrl(defaultFile)} />
              )}
            </span>
          </div>
        )
      }

      {
        // File name PREVIEW
        fileToUpload
        && fileType !== 'image/png'
        && fileType !== 'image/gif'
        && fileType !== 'image/webp'
        && fileType !== 'image/jpg'
        && fileType !== 'image/jpeg' && (
          <div className="control-upload__file">
            <span className="control-upload__file__extension">
              <VpIcon name="attachment" size={20} />
              {' '}
              {fileName}
            </span>
          </div>
        )
      }

      {
        // CANCELLING
        !fromProfile && (fileToUpload || defaultFile) && (
          <button
            type="button"
            className="control-upload__cancel"
            onClick={resetState}
            onKeyDown={e => e.key === keyboardKeys.ENTER && resetState()}
            title={t('PROFILE.GLOBAL.DELETE')}
            tabIndex={0}
          >
            <VpIcon name="close-circle" size={16} />
            <span>{t('PROFILE.GLOBAL.DELETE')}</span>
          </button>
        )
      }

      {
        // PLACEHOLDER
        !fileToUpload && !defaultFile && (
          <span className="control-upload__placeholder">
            <span className="control-placeholder vp-caption">
              <strong>
                {t('FILE_UPLOADER.ERROR_2', { sizeLimit: fileSizeMegaBytesDefaultLimit })}
                {' '}
              </strong>
              {t(fileUploaderTitle)}
            </span>
          </span>
        )
      }
    </div>
  );
};
